import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AccountResponsePayload, fetchAccount, ImageInfo } from '../api/my-account';
import getRoleTranslationKey from '../core/security/get-role-translation-key';
import HighestRoleExtractor from '../core/security/highest-role-extractor';
import Identity from '../core/security/identity';
import Role from '../core/security/role';
import useThrowAsyncError from '../hooks/useThrowAsyncError';
import IdentityContext from './IdentityContext';

const getBackgroundImage = (background: Partial<ImageInfo>) => {
  const { customImage, predefinedImage } = background;

  if (customImage) return `${process.env.REACT_APP_P_API_URL}/api/files/${customImage}`;

  return `${process.env.PUBLIC_URL}/images/background/${predefinedImage}`;
};

const getAvatarImage = (avatar: Partial<ImageInfo>) => {
  const { customImage, predefinedImage } = avatar;

  if (customImage) return `${process.env.REACT_APP_P_API_URL}/api/files/${customImage}`;

  if (predefinedImage) return `${process.env.PUBLIC_URL}/images/avatar/${predefinedImage}`;

  return `${process.env.PUBLIC_URL}/images/avatar/avatarimage_default.jpg`;
};

const getDisplayName = ({
  nickName,
  firstName,
  lastName,
}: {
  nickName?: string;
  firstName?: string;
  lastName?: string;
}) => {
  if (nickName) return nickName;

  if (firstName && lastName) {
    return [firstName, lastName].join(' ');
  }

  return undefined;
};

export type AccountPayload = Partial<Omit<AccountResponsePayload, 'avatar' | 'background'>> & {
  avatar?: string | Partial<ImageInfo>;
  background?: string | Partial<ImageInfo>;
};

export const toAccount = ({
  payload,
  accountInfo = defaultAccount,
  identity,
}: {
  payload: AccountPayload;
  accountInfo?: Account;
  identity: Identity;
}): AccountContextValue['account'] => {
  const avatar = payload.avatar
    ? typeof payload.avatar === 'object'
      ? getAvatarImage(payload.avatar)
      : payload.avatar
    : accountInfo.avatar;

  const backgroundImage = payload.background
    ? typeof payload.background === 'object'
      ? getBackgroundImage(payload.background)
      : payload.background
    : accountInfo.backgroundImage;

  const highestRole = HighestRoleExtractor.extract(identity.roles);

  return {
    avatar,
    backgroundImage,
    role: getRoleTranslationKey(highestRole, identity.isAuthor),
    displayName: getDisplayName(payload) ?? accountInfo.displayName,
    oauthIdentities: payload.identities ?? accountInfo.oauthIdentities,
    unsyncedSmartschoolUrls: payload.unsyncedSmartschoolUrls ?? accountInfo.unsyncedSmartschoolUrls,
    smartschoolSchools: payload.smartschoolSchools ?? accountInfo.smartschoolSchools,
    school: payload.school ?? accountInfo.school,
    nickName: payload.nickName ?? '',
    isCandidateTeacher: payload.isCandidateTeacher ?? accountInfo.isCandidateTeacher,
    hasBaollLicense: payload.hasBaollLicense ?? accountInfo.hasBaollLicense,
    existingPassword: payload.existingPassword ?? accountInfo.existingPassword,
    primaryEmail: payload.primaryEmail ?? accountInfo.primaryEmail,
    verifiedEmail: payload.verifiedEmail ?? accountInfo.verifiedEmail,
    provider: payload.provider,
    firstName: payload.firstName ?? accountInfo.firstName,
    lastName: payload.lastName ?? accountInfo.lastName,
  };
};

type Account = {
  firstName?: string;
  lastName?: string;
  avatar: string;
  role: string;
  displayName?: string;
  backgroundImage?: string;
  nickName?: string;
  oauthIdentities?: AccountResponsePayload['identities'];
  school?: AccountResponsePayload['school'];
  isCandidateTeacher: boolean;
  hasBaollLicense: boolean;
  provider?: AccountResponsePayload['provider'];
} & Pick<
  AccountResponsePayload,
  'unsyncedSmartschoolUrls' | 'smartschoolSchools' | 'primaryEmail' | 'existingPassword' | 'verifiedEmail'
>;

export type AccountContextValue = {
  account: Account;
  isAccountLoaded?: boolean;
  refreshAccount: () => void;
};

const defaultAccount: Account = {
  avatar: `${process.env.PUBLIC_URL}/images/avatar/anonymous_avatar.png`,
  role: getRoleTranslationKey(Role.STUDENT),
  displayName: undefined,
  unsyncedSmartschoolUrls: [],
  smartschoolSchools: [],
  nickName: '',
  school: undefined,
  isCandidateTeacher: false,
  hasBaollLicense: false,
  primaryEmail: undefined,
  verifiedEmail: undefined,
  existingPassword: undefined,
  provider: undefined,
};

export const defaultAccountContextValue: AccountContextValue = {
  account: defaultAccount,
  isAccountLoaded: false,
  refreshAccount: () => {},
};

const AccountContext = createContext<AccountContextValue>(defaultAccountContextValue);

export default AccountContext;

export function AccountContextProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const { identity } = useContext(IdentityContext);

  const [accountInfo, setAccountInfo] = useState<Account>(defaultAccount);
  const [isAccountLoaded, setIsAccountLoaded] = useState(false);

  const handleError = useThrowAsyncError();

  const getAccount = useCallback(
    async (acct?: AccountPayload) => {
      if (acct) {
        setAccountInfo(accountInfo => toAccount({ payload: acct, accountInfo, identity }));
        return;
      }

      try {
        const account = await fetchAccount();

        setAccountInfo(toAccount({ payload: account, identity }));
      } catch (err) {
        handleError(err);
      } finally {
        setIsAccountLoaded(true);
      }
    },
    [identity, handleError],
  );

  useEffect(() => {
    if (!identity.isAuthenticated) return;

    getAccount();
  }, [getAccount, identity]);

  const accountValue = useMemo(
    () => ({
      account: accountInfo,
      isAccountLoaded,
      refreshAccount: getAccount,
    }),
    [accountInfo, isAccountLoaded, getAccount],
  );

  return <AccountContext.Provider value={accountValue}>{children}</AccountContext.Provider>;
}
